import { useCurrentElement, useScroll } from '@vueuse/core';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import type { Ref } from 'vue';
import { onMounted, onUnmounted, ref, watch } from 'vue';

interface LongScrollEvent {
  verticalScrollPosition: number;
}

interface LongScrollComposable {
  longScrollEvent: Observable<LongScrollEvent>;
}

export function useLongScroll(
  element: Ref<
    HTMLElement | undefined | null
  > = useCurrentElement<HTMLElement>()
): LongScrollComposable {
  const CONTINUOUS_SCROLL_VALUE_PX = 96; // HACK: neest to be at least as price ribbon height to not breack mobile behaviour

  const longScrollEvent = new Subject<LongScrollEvent>();
  const currentScrollLength = ref<number>(0);
  const hasAlreadyEmitedEvent = ref<boolean>(false);

  onMounted(() => {
    if (!element.value) {
      return;
    }

    const scrollableElement = getScrollableElement(element.value);

    const { y: verticalScroll, directions } = useScroll(scrollableElement);

    // NOTE: Component emits long-scroll event, when current value exceeds threshold
    const stopScrollPositionWatch = watch(
      verticalScroll,
      (verticalScrollPosition) => {
        if (
          currentScrollLength.value > CONTINUOUS_SCROLL_VALUE_PX &&
          !hasAlreadyEmitedEvent.value
        ) {
          longScrollEvent.next({ verticalScrollPosition });
          hasAlreadyEmitedEvent.value = true;
        } else {
          currentScrollLength.value += 1;
        }
      }
    );

    // NOTE: Component resets current scroll value each time user stops scrolling
    const stopScrollDirectionWatch = watch(directions, ({ bottom }) => {
      if (!bottom) {
        currentScrollLength.value = 0;
        hasAlreadyEmitedEvent.value = false;
      }
    });

    onUnmounted(() => {
      stopScrollPositionWatch();
      stopScrollDirectionWatch();
      longScrollEvent.unsubscribe();
    });
  });

  return {
    longScrollEvent,
  };
}

function getScrollableElement(element: HTMLElement): HTMLElement | Window {
  const isScrollable = ['scroll', 'auto'].includes(
    getComputedStyle(element).overflowY
  );

  if (isScrollable) {
    return element;
  }

  const parentElement = element.parentElement;

  if (!parentElement) {
    return window;
  }

  return getScrollableElement(element.parentElement);
}
